import React from 'react';
import { cn } from '@/lib/utils';
import FadeIn from './animations/FadeIn';

interface ContactFormProps {
  className?: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ className }) => {
  return (
    <section id="contact" className={cn('py-20 bg-gray-900 text-white', className)}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <FadeIn>
              <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">
                Order Your Custom Sparks Website
              </h2>
              <p className="text-lg text-gray-300 mb-8">
                Ready to create something special? Fill out our form and we{'\''}ll create a personalized 
                Sparks website that will make your proposal unforgettable.
              </p>
            </FadeIn>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <FadeIn delay={200}>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6">
                <div className="text-center mb-4">
                  <div className="text-2xl font-bold text-pink-400 mb-2">$50</div>
                  <div className="text-lg font-semibold text-white mb-2">Template Package</div>
                  <div className="text-sm text-gray-400">No customization needed</div>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-pink-500 flex-shrink-0 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-sm text-gray-300">Pre-made romantic template</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-pink-500 flex-shrink-0 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-sm text-gray-300">Your partner{'\''}s name included</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-pink-500 flex-shrink-0 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-sm text-gray-300">Mobile responsive design</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-pink-500 flex-shrink-0 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-sm text-gray-300">Ready in 24 hours</span>
                  </div>
                </div>

                <a 
                  href="https://forms.gle/7oQv9e4ojMBTFJZr9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 rounded-md font-medium transition-all inline-block text-center"
                >
                  Choose Template Package
                </a>
              </div>
            </FadeIn>

            <FadeIn delay={300}>
              <div className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 backdrop-blur-sm border border-pink-500/20 rounded-lg p-6 relative">
                <div className="absolute top-3 right-3 bg-pink-500 text-white text-xs px-2 py-1 rounded-full">
                  POPULAR
                </div>
                
                <div className="text-center mb-4">
                  <div className="text-2xl font-bold text-pink-400 mb-2">Custom Quote</div>
                  <div className="text-lg font-semibold text-white mb-2">Custom Package</div>
                  <div className="text-sm text-gray-400">Discuss with developer</div>
                </div>
                
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-pink-500 flex-shrink-0 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-sm text-gray-300">Fully customized design</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-pink-500 flex-shrink-0 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-sm text-gray-300">Your own images & colors</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-pink-500 flex-shrink-0 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-sm text-gray-300">Custom animations & effects</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-pink-500 flex-shrink-0 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                    <span className="text-sm text-gray-300">Developer consultation</span>
                  </div>
                </div>

                <a 
                  href="https://forms.gle/7oQv9e4ojMBTFJZr9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-6 py-3 rounded-md font-medium transition-all inline-block text-center"
                >
                  Discuss Custom Package
                </a>
              </div>
            </FadeIn>
          </div>

          <div className="text-center">
            <FadeIn delay={400}>
              <div className="max-w-lg mx-auto">
                <h3 className="text-xl font-semibold mb-4 text-white">Ready to Start?</h3>
                <p className="text-gray-300 mb-6">
                  Click either package above to fill out our Google Form and we{'\''}ll get back to you within 24 hours!
                </p>
                <div className="text-sm text-gray-400">
                  💕 Perfect for proposals, anniversaries, or just showing you care
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;